/** Privacy-first transformation for UI telemetry payloads.
 *
 * Patient data is removed, not pseudonymised. Hashing low-entropy clinical text
 * or identifiers would still permit dictionary attacks, so the default output
 * retains only a redaction marker and original string length.
 */

export interface RedactionMarker {
  readonly redacted: true;
  readonly reason: "sensitive-key" | "unapproved-string" | "excessive-length";
  readonly length?: number;
}

export interface TelemetryPrivacyPolicy {
  readonly maximumSafeStringLength: number;
  readonly maximumDepth: number;
  readonly maximumCollectionLength: number;
}

export const DEFAULT_TELEMETRY_PRIVACY_POLICY: TelemetryPrivacyPolicy = Object.freeze({
  maximumSafeStringLength: 160,
  maximumDepth: 8,
  maximumCollectionLength: 100,
});

const SENSITIVE_KEYS = new Set([
  "address",
  "caseid",
  "clinicalnote",
  "dateofbirth",
  "diagnosistext",
  "dob",
  "email",
  "freetext",
  "languageinput",
  "name",
  "note",
  "notes",
  "observationvalue",
  "patientid",
  "patientidentifier",
  "patientname",
  "phone",
  "rawtext",
  "sourceexcerpt",
  "sourcespantext",
  "subject",
  "symptomtext",
  "text",
]);

const SAFE_STRING_KEYS = new Set([
  "action",
  "actiontype",
  "component",
  "errorcode",
  "eventname",
  "eventidentifier",
  "interfaceversion",
  "knowledgeversion",
  "outcome",
  "requestidentifier",
  "caseidentifierhash",
  "route",
  "routecategory",
  "routetemplate",
  "severity",
  "status",
  "validationcode",
]);

const SAFE_STRING_VALUE = /^[A-Za-z0-9_./:{}-]+$/;

function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function marker(
  reason: RedactionMarker["reason"],
  value?: unknown,
): RedactionMarker {
  const length = typeof value === "string" ? value.length : undefined;
  return Object.freeze({ redacted: true, reason, ...(length === undefined ? {} : { length }) });
}

export function isSensitiveTelemetryKey(key: string): boolean {
  return SENSITIVE_KEYS.has(normaliseKey(key));
}

function isApprovedSafeString(key: string, value: string): boolean {
  return SAFE_STRING_KEYS.has(normaliseKey(key)) && SAFE_STRING_VALUE.test(value);
}

function redactValue(
  value: unknown,
  policy: TelemetryPrivacyPolicy,
  key: string,
  depth: number,
  seen: WeakSet<object>,
): unknown {
  if (isSensitiveTelemetryKey(key)) {
    return marker("sensitive-key", value);
  }
  if (depth > policy.maximumDepth) {
    return marker("excessive-length");
  }
  if (value === null || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    if (!isApprovedSafeString(key, value)) {
      return marker("unapproved-string", value);
    }
    if (value.length > policy.maximumSafeStringLength) {
      return marker("excessive-length", value);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, policy.maximumCollectionLength)
      .map((item) => redactValue(item, policy, key, depth + 1, seen));
  }
  if (typeof value === "object") {
    if (seen.has(value)) {
      return marker("excessive-length");
    }
    seen.add(value);
    const output: Record<string, unknown> = {};
    for (const property of Object.keys(value).sort()) {
      output[property] = redactValue(
        (value as Record<string, unknown>)[property],
        policy,
        property,
        depth + 1,
        seen,
      );
    }
    seen.delete(value);
    return output;
  }
  return marker("unapproved-string");
}

export function redactTelemetryPayload(
  payload: Readonly<Record<string, unknown>>,
  policy: TelemetryPrivacyPolicy = DEFAULT_TELEMETRY_PRIVACY_POLICY,
): Readonly<Record<string, unknown>> {
  if (policy.maximumSafeStringLength <= 0) {
    throw new RangeError("maximumSafeStringLength must be positive");
  }
  if (policy.maximumDepth < 0 || policy.maximumCollectionLength <= 0) {
    throw new RangeError("telemetry privacy limits are invalid");
  }
  return Object.freeze(
    redactValue(payload, policy, "payload", 0, new WeakSet()) as Record<string, unknown>,
  );
}

export interface PrivacyViolation {
  readonly path: string;
  readonly reason: string;
}

function isRedactionMarker(value: unknown): value is RedactionMarker {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  const hasOnlyMarkerFields = Object.keys(candidate).every((key) =>
    key === "redacted" || key === "reason" || key === "length"
  );
  const validReason =
    candidate.reason === "sensitive-key" ||
    candidate.reason === "unapproved-string" ||
    candidate.reason === "excessive-length";
  const validLength =
    candidate.length === undefined ||
    (Number.isInteger(candidate.length) && (candidate.length as number) >= 0);
  return candidate.redacted === true && validReason && validLength && hasOnlyMarkerFields;
}

function findViolations(
  value: unknown,
  path: string,
  key: string,
  violations: PrivacyViolation[],
): void {
  if (isRedactionMarker(value)) {
    return;
  }
  if (isSensitiveTelemetryKey(key)) {
    if (!isRedactionMarker(value)) {
      violations.push({ path, reason: "sensitive telemetry field was not redacted" });
    }
    return;
  }
  if (typeof value === "string" && !isApprovedSafeString(key, value)) {
    violations.push({ path, reason: "unapproved free-form string in telemetry" });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => findViolations(item, `${path}[${index}]`, key, violations));
    return;
  }
  if (typeof value === "object" && value !== null) {
    for (const property of Object.keys(value).sort()) {
      findViolations(
        (value as Record<string, unknown>)[property],
        `${path}.${property}`,
        property,
        violations,
      );
    }
  }
}

export function findTelemetryPrivacyViolations(value: unknown): readonly PrivacyViolation[] {
  const violations: PrivacyViolation[] = [];
  findViolations(value, "$", "payload", violations);
  return Object.freeze(violations);
}
